var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import StylePropType from 'react-style-proptype';
import { horizon } from '../colors';
import CarouselSlide from './CarouselSlide';
import CarouselControl from './CarouselControl';
import CarouselIndicator from './CarouselIndicator';
import CarouselLoading from './CarouselLoading';
import CarouselFallback from './CarouselFallback';
const THUMBNAIL_WIDTH = 70;
const getCarouselWidthForThumbs = numberOfThumbs => 40 + THUMBNAIL_WIDTH * numberOfThumbs;
const sizeMap = {
    xs: { maxThumbs: 3, pxWidth: getCarouselWidthForThumbs(3) },
    s: { maxThumbs: 4, pxWidth: getCarouselWidthForThumbs(4) },
    m: { maxThumbs: 5, pxWidth: getCarouselWidthForThumbs(5) },
    l: { maxThumbs: 6, pxWidth: getCarouselWidthForThumbs(6) },
    xl: { maxThumbs: 7, pxWidth: getCarouselWidthForThumbs(7) },
};
const Carousel = (_a) => {
    var { loading, size, pxSize, minimal, children, onCarouselClick, fallbackPreview, hideIndicators, showZoomOverlay, zoomOverlayContent, className, style, landscape, currentSlide, onSlideChanged } = _a, rest = __rest(_a, ["loading", "size", "pxSize", "minimal", "children", "onCarouselClick", "fallbackPreview", "hideIndicators", "showZoomOverlay", "zoomOverlayContent", "className", "style", "landscape", "currentSlide", "onSlideChanged"]);
    const total = React.Children.count(children);
    const pixels = size && !minimal ? sizeMap[size].pxWidth : pxSize;
    const allowNavigation = (minimal && total > 1) || (!minimal && total > sizeMap[size].maxThumbs);
    const isLandscape = minimal ? false : landscape;
    /**
     * State and control for slides
     */
    const [slideState, setSlideState] = useState({
        currentSlide: currentSlide > total - 1 ? 0 : currentSlide,
        previousSlide: null,
        transitioning: false,
        timer: null,
        direction: '',
    });
    useEffect(() => {
        setSlideState(previousState => {
            if (currentSlide !== undefined && previousState.currentSlide !== currentSlide) {
                const slideNumber = (currentSlide < 0 || currentSlide >= total) ? previousState.currentSlide : currentSlide;
                return Object.assign(Object.assign({}, previousState), { currentSlide: slideNumber });
            }
            return previousState;
        });
    }, [currentSlide, total]);
    const clearPendingAnimation = () => {
        if (slideState.timer) {
            clearTimeout(slideState.timer);
        }
    };
    const endAnimation = () => setTimeout(() => setSlideState(prevState => (Object.assign(Object.assign({}, prevState), { previousSlide: null, transitioning: false }))), 600);
    const goToSlide = (nextSlide, direction) => {
        if (nextSlide === slideState.currentSlide) {
            return;
        }
        clearPendingAnimation();
        setSlideState(prevState => (Object.assign(Object.assign({}, prevState), { direction: direction || nextSlide > slideState.currentSlide ? 'next' : 'prev', previousSlide: prevState.currentSlide, currentSlide: nextSlide, transitioning: true, timer: endAnimation() })));
        onSlideChanged && onSlideChanged(nextSlide);
    };
    const goToNextSlide = () => {
        let nextSlide = slideState.currentSlide + 1;
        if (nextSlide >= React.Children.count(children)) {
            nextSlide = 0;
        }
        goToSlide(nextSlide, 'next');
    };
    const goToPreviousSlide = () => {
        const nextSlide = slideState.currentSlide > 0 ? slideState.currentSlide - 1 : React.Children.count(children) - 1;
        goToSlide(nextSlide, 'prev');
    };
    /**
     * State and controls for thumbnails
     */
    const [thumbsPosition, setThumbsPosition] = useState(0);
    const goToThumbs = direction => {
        let newThumbsPosition = thumbsPosition;
        const totalLength = React.Children.count(children) * THUMBNAIL_WIDTH;
        const thumbsWidth = sizeMap[size].maxThumbs * THUMBNAIL_WIDTH;
        const loopPosition = direction === 'next' ? 0 : (Math.floor(totalLength / thumbsWidth) - 1) * thumbsWidth * -1;
        direction === 'next' ? (newThumbsPosition -= thumbsWidth) : (newThumbsPosition += thumbsWidth);
        newThumbsPosition = Math.abs(newThumbsPosition) >= totalLength || newThumbsPosition > 0 ? loopPosition : newThumbsPosition;
        setThumbsPosition(newThumbsPosition);
    };
    const goToNextThumbs = () => goToThumbs('next');
    const goToPreviousThumbs = () => goToThumbs('prev');
    const slides = React.Children.map(children, (child, i) => (React.createElement(CarouselSlide, { active: i === slideState.currentSlide, index: i, animateOut: i === slideState.previousSlide && slideState.transitioning, animateIn: i === slideState.currentSlide && slideState.previousSlide !== null && slideState.transitioning, direction: slideState.direction, onCarouselClick: onCarouselClick, showZoomOverlay: showZoomOverlay, zoomOverlayContent: zoomOverlayContent }, child)));
    const componentStyle = isLandscape
        ? { height: `${pixels}px`, display: 'flex', flexDirection: 'row-reverse', width: 'fit-content' }
        : { width: `${pixels}px` };
    return (React.createElement("div", Object.assign({ className: className, style: Object.assign(Object.assign({}, componentStyle), style) }, rest),
        React.createElement("div", { style: isLandscape ? { width: `${pixels}px` } : { height: `${pixels}px` } },
            React.createElement("div", { className: "carousel slide" },
                React.createElement("div", { className: "carousel-inner", role: "listbox" }, loading ? React.createElement(CarouselLoading, { size: pixels }) : slides && slides.length ? slides : fallbackPreview))),
        !hideIndicators && (React.createElement("div", { className: `carousel-navigation${minimal ? ' carousel-navigation-small' : ''}${isLandscape ? ' carousel-navigation-vertical' : ''}` },
            React.createElement(CarouselControl, { disabled: !allowNavigation, onClick: minimal ? goToPreviousSlide : goToPreviousThumbs, large: !minimal, direction: isLandscape ? 'up' : 'left', hintText: "Previous" }),
            React.createElement("ol", { className: "carousel-indicators", style: minimal ? { height: 'auto', overflow: 'visible' } : {} }, total && !minimal ? (React.Children.map(children, (child, i) => (React.createElement(CarouselIndicator, { slideNumber: i, active: slideState.currentSlide === i, goToSlide: goToSlide, offset: isLandscape ? { top: thumbsPosition } : { left: thumbsPosition } }, loading ? null : child)))) : minimal ? (React.createElement("div", { className: "small" }, total > 0 ? (`${slideState.currentSlide + 1} / ${total}`) : ('– / –'))) : (React.createElement("li", { disabled: true, style: { backgroundColor: horizon } }))),
            React.createElement(CarouselControl, { disabled: !allowNavigation, onClick: minimal ? goToNextSlide : goToNextThumbs, large: !minimal, direction: isLandscape ? 'down' : 'right', hintText: "Next" })))));
};
Carousel.propTypes = {
    className: PropTypes.string,
    size: PropTypes.oneOf(['xs', 's', 'm', 'l', 'xl']),
    pxSize: PropTypes.number,
    minimal: PropTypes.bool,
    children: PropTypes.node,
    loading: PropTypes.bool,
    onCarouselClick: PropTypes.func,
    currentSlide: PropTypes.number,
    fallbackPreview: PropTypes.node,
    landscape: PropTypes.bool,
    onSlideChanged: PropTypes.func,
    hideIndicators: PropTypes.bool,
    showZoomOverlay: PropTypes.bool,
    style: StylePropType,
    zoomOverlayContent: PropTypes.node,
};
Carousel.defaultProps = {
    size: 'm',
    pxSize: 200,
    minimal: false,
    currentSlide: 0,
    fallbackPreview: React.createElement(CarouselFallback, null),
    landscape: false,
    hideIndicators: false,
    showZoomOverlay: false,
};
export default Carousel;
